import React, { useEffect } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useWatch } from "react-hook-form";
import { twJoin, twMerge } from "tailwind-merge";

import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { OperatorDropdown } from "src/base-components/OperatorDropdown";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { SharedRowProps } from "src/clauseBlock/ClauseBlock";
import {
  CLAUSE_BLOCK_SIZES,
  JunctionDropdown,
} from "src/clauseBlock/JunctionDropdown";
import { Clause, Operators } from "src/clients/flow-api";
import { IconButton } from "src/design-system/Icon";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";

export type ClauseName = `groups.${number}.clauses.${number}`;

export type JunctionName = `groups.${number}.junction`;

type Props = SharedRowProps & {
  idLeft: string;
  idRight: string;
  length: number;
  index: number;
  clauseName: ClauseName;
  junctionName: JunctionName;
  onDeleteClause: () => void;
  errorLeft?: string;
  errorRight?: string;
  sortableId?: string;
};

const getJunctionDataLoc = (index: number) => `clause-${index}-junction`;

export const ClauseRow: React.FC<Props> = ({
  idLeft,
  idRight,
  index,
  clauseName,
  junctionName,
  onDeleteClause,
  length,
  control,
  immutable,
  setValue,
  size,
  fieldPlaceholder,
  valuePlaceholder,
  allowDeletingAllClauses,
  errorLeft,
  errorRight,
  lazyInputs,
  sortableId,
}) => {
  const { isDragging, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: sortableId ?? clauseName,
      disabled: !sortableId || immutable,
    });

  const renderJunction = () => {
    if (index === 0) {
      return (
        <div
          className={twJoin(
            "shrink-0 font-inter-medium-12px text-gray-700",
            CLAUSE_BLOCK_SIZES[size].junctionWidth,
          )}
        >
          If
        </div>
      );
    }
    return (
      <Controller
        control={control}
        name={junctionName}
        render={(props) => {
          if (index !== 1) {
            return (
              <div
                className={twJoin(
                  "flex h-8 shrink-0 items-center pl-2.5 font-inter-medium-12px text-gray-700",
                  CLAUSE_BLOCK_SIZES[size].junctionWidth,
                )}
                data-loc={getJunctionDataLoc(index)}
              >
                {props.field.value}
              </div>
            );
          }
          return (
            <JunctionDropdown
              dataLoc={getJunctionDataLoc(index)}
              disabled={immutable}
              size={size}
              value={props.field.value}
              onChange={props.field.onChange}
            />
          );
        }}
      />
    );
  };

  const operator = useWatch({ name: `${clauseName}.operator`, control });
  const isUnaryOperator = [Operators.IS_NONE, Operators.IS_NOT_NONE].some(
    (x) => x === operator,
  );
  const isLeftHighlighted = useIsFieldHighlighted(idLeft);
  const isRightHighlighted = useIsFieldHighlighted(idRight);

  useEffect(() => {
    if (isUnaryOperator) {
      setValue(`${clauseName}.right`, "");
    }
  }, [isUnaryOperator, setValue, clauseName]);

  return (
    <div className="my-2 flex grow-0 items-center gap-x-2">
      {renderJunction()}
      <div
        ref={sortableId ? setNodeRef : undefined}
        className={twMerge(
          "flex w-full min-w-0 items-center gap-x-2",
          isDragging && "invisible",
        )}
        style={
          sortableId
            ? { transform: CSS.Transform.toString(transform), transition }
            : undefined
        }
      >
        {sortableId && (
          <ReorderHandle
            className="-mx-1"
            immutable={immutable}
            listeners={listeners}
          />
        )}
        <div className="flex w-full gap-x-2">
          <Controller
            control={control}
            name={`${clauseName}.left`}
            render={({ field }) => (
              <AutocompleteCodeInput
                dataLoc={`clause-${index}-left`}
                disabled={immutable}
                error={errorLeft}
                isSearchHighlighted={isLeftHighlighted}
                lazy={lazyInputs}
                placeholder={fieldPlaceholder}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name={`${clauseName}.right`}
            render={({ field }) => (
              <AutocompleteCodeInput
                dataLoc={`clause-${index}-right`}
                disabled={immutable}
                error={errorRight}
                isSearchHighlighted={isRightHighlighted}
                lazy={lazyInputs}
                placeholder={!isUnaryOperator ? valuePlaceholder : ""}
                readOnly={isUnaryOperator}
                value={field.value}
                onChange={field.onChange}
              >
                <Controller
                  control={control}
                  name={`${clauseName}.operator`}
                  render={(props) => (
                    <OperatorDropdown
                      disabled={immutable}
                      value={operator}
                      onChange={props.field.onChange}
                    />
                  )}
                />
              </AutocompleteCodeInput>
            )}
          />
          {(length !== 1 || allowDeletingAllClauses) && (
            <IconButton
              aria-label="Delete condition"
              disabled={immutable}
              icon={faTrashCan}
              size="xs"
              onClick={onDeleteClause}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const noop = () => undefined;

export const ClauseDragOverlay: React.FC<{ clause: Clause }> = ({ clause }) => {
  const isUnaryOperator = [Operators.IS_NONE, Operators.IS_NOT_NONE].some(
    (x) => x === clause.operator,
  );

  return (
    <div className="flex items-center gap-x-2">
      <ReorderHandle className="-mx-1" listeners={undefined} isGrabbing />
      <div className="flex w-full gap-x-2">
        <AutocompleteCodeInput lazy value={clause.left} onChange={noop} />
        <AutocompleteCodeInput
          lazy
          readOnly={isUnaryOperator}
          value={clause.right}
          onChange={noop}
        >
          <OperatorDropdown value={clause.operator} onChange={noop} />
        </AutocompleteCodeInput>
        <IconButton
          aria-label="Delete condition"
          icon={faTrashCan}
          size="xs"
          onClick={noop}
        />
      </div>
    </div>
  );
};
